/**
 * Common CSS class utilities for consistent styling across components
 */

export const buttonClasses = {
  // Primary button styles
  primary: "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
  
  // Secondary button styles
  secondary: "px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
  
  // Success button styles
  success: "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500",
  
  // Danger button styles
  danger: "px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500",
  
  // Small button variant
  small: "inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
};

export const inputClasses = {
  // Standard text input
  text: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500",
  
  // Large text input (login/forms)
  large: "w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm",
  
  // Textarea
  textarea: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500",
  
  // Select dropdown
  select: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500",
  
  // Checkbox
  checkbox: "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",
  
  // Radio button
  radio: "h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
};

export const badgeClasses = {
  // Individual user badge
  individual: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800",
  
  // Company user badge
  company: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800",
  
  // Success badge
  success: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800",
  
  // Warning badge
  warning: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800",
  
  // Error badge
  error: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
};

export const cardClasses = {
  // Standard card
  base: "bg-white overflow-hidden shadow rounded-lg",
  
  // Card with hover effect
  hover: "bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200",
  
  // Card header
  header: "px-4 py-5 sm:px-6",
  
  // Card body
  body: "px-4 py-5 sm:p-6",
  
  // Card footer
  footer: "px-4 py-4 sm:px-6"
};

export const listClasses = {
  // List container
  container: "bg-white shadow overflow-hidden sm:rounded-md",
  
  // List item
  item: "px-4 py-4 sm:px-6",
  
  // List item with border
  itemBordered: "border-t border-gray-200 px-4 py-4 sm:px-6",
  
  // List item hover effect
  itemHover: "px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors"
};

export const gridClasses = {
  // Standard 2-column grid
  cols2: "grid grid-cols-1 sm:grid-cols-2 gap-4",
  
  // Standard 3-column grid
  cols3: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
  
  // Definition list grid
  definition: "bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
};

export const textClasses = {
  // Section headings
  heading: "text-lg leading-6 font-medium text-gray-900",
  
  // Subheadings
  subheading: "text-sm font-medium text-gray-700",
  
  // Description text
  description: "mt-1 text-sm text-gray-500",
  
  // Body text
  body: "text-sm text-gray-900",
  
  // Muted text
  muted: "text-sm text-gray-500"
};

export const spacingClasses = {
  // Standard section spacing
  section: "space-y-6",
  
  // Form spacing
  form: "space-y-4",
  
  // Button group spacing
  buttonGroup: "flex space-x-3",
  
  // Vertical stack spacing
  stack: "space-y-4"
};

/**
 * Utility function to combine classes conditionally
 */
export const classNames = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Common loading spinner classes
 */
export const loadingClasses = {
  spinner: "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin",
  container: "flex items-center justify-center"
};

/**
 * Centralized styling utilities to eliminate repeated className patterns
 */

// Modal overlay utilities
export const getModalOverlayClasses = (zIndex: number = 10) => 
  `fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-${zIndex}`;

export const getModalContentClasses = (maxWidth: string = 'max-w-md') => 
  `bg-white rounded-lg shadow-xl ${maxWidth} w-full p-6`;

// Button styling utilities
export const getPrimaryButtonClasses = (size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm', 
    lg: 'px-6 py-3 text-base'
  };
  
  return `inline-flex items-center ${sizes[size]} border border-transparent rounded-md shadow-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`;
};

export const getSecondaryButtonClasses = (size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  return `inline-flex items-center ${sizes[size]} border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`;
};

export const getDangerButtonClasses = (size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  return `inline-flex items-center ${sizes[size]} border border-transparent rounded-md shadow-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors`;
};

// Input styling utilities
export const getInputClasses = (hasError: boolean = false) => 
  `mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
    hasError 
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300'
  }`;

export const getSelectClasses = (hasError: boolean = false) => 
  `mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
    hasError 
      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300'
  }`;

// Status badge utilities
export const getStatusBadgeClasses = (status: 'success' | 'warning' | 'error' | 'info' | 'neutral') => {
  const styles = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800', 
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    neutral: 'bg-gray-100 text-gray-800'
  };
  
  return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`;
};

// Card utilities
export const getCardClasses = (variant: 'default' | 'elevated' | 'bordered' = 'default') => {
  const variants = {
    default: 'bg-white rounded-lg shadow-sm border border-gray-200',
    elevated: 'bg-white rounded-xl shadow-lg border border-gray-200',
    bordered: 'bg-white rounded-lg border-2 border-gray-200'
  };
  
  return variants[variant];
};

// Navigation utilities  
export const getNavTabClasses = (isActive: boolean) =>
  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
    isActive 
      ? 'bg-blue-100 text-blue-700' 
      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
  }`;

// Pagination utilities
export const getPaginationButtonClasses = (isDisabled: boolean = false) =>
  `relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors ${
    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  }`;

// Layout utilities
export const getContainerClasses = (size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'lg') => {
  const sizes = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl', 
    lg: 'max-w-7xl',
    xl: 'max-w-8xl',
    full: 'max-w-full'
  };
  
  return `${sizes[size]} mx-auto px-4 sm:px-6 lg:px-8`;
};

// Grid utilities
export const getGridClasses = (cols: number, gap: number = 4) =>
  `grid grid-cols-1 md:grid-cols-${Math.min(cols, 2)} lg:grid-cols-${Math.min(cols, 4)} xl:grid-cols-${cols} gap-${gap}`;

// Animation utilities
export const getLoadingSpinnerClasses = (size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8', 
    lg: 'h-12 w-12'
  };
  
  return `animate-spin rounded-full ${sizes[size]} border-b-2 border-blue-600`;
}; 